import { STELLING, STELLING_OFFSET, WERKGANG, hotspotStand } from './indeling'
import type { Indeling } from './indeling'
import { VORK_MIDDEN } from './trucks/KMatic'
import { HOTSPOTS } from '../content/content'

type Model = (typeof HOTSPOTS)[number]['truck']

/**
 * Waar elke truck rijdt, en voor de K- en R-MATIC: in welke vakken ze
 * pallets wegzetten en ophalen. `Magazijn` geeft het aan de trucks, `Lading`
 * houdt die vakken vrij.
 */

/**
 * Waar een truck in de stelling werkt, gemeten vanaf zijn oorsprong langs
 * de rijrichting: bij de K-Matic het midden tussen de vorken, bij de R-Matic
 * het draaipunt.
 */
const WERKPUNT: Partial<Record<Model, number>> = {
  'K-Matic': VORK_MIDDEN,
  'R-Matic': 0,
}
/**
 * Hoe dicht zo'n truck bij de hotspotcamera mag werken. De K-Matic is 6,4 m
 * hoog: dichterbij dan 10 m is hij in beeld alleen nog een mast.
 */
const MIN_AFSTAND: Partial<Record<Model, number>> = {
  'K-Matic': 10,
  'R-Matic': 6.5,
}
/**
 * Hoeveel vakken werkpunt B verder ligt dan A, in volgorde van voorkeur.
 * Vier vakken is 10,8 m: ver genoeg om te zien dat hij de pallet
 * verplaatst, dichtbij genoeg om B nog in beeld te hebben.
 */
const AFSTANDEN = [4, 3, 5, 2]

/** Twee werkpunten in de stelling, elk twee vakken boven elkaar (niveau 1 en 2). */
export interface Werkpunten {
  /** x van het midden van vak A en vak B. */
  a: number
  b: number
  /** Nummers van die vakken. */
  vakken: [number, number]
  /** z van het midden van de stelling waarin hij werkt. */
  stelling: number
}

export interface Werkgang {
  model: Model
  /** z van het gangpad. */
  gangpad: number
  anker: number
  bereik: number
  snelheid: number
  fase: number
  richting: 1 | -1
  /** Alleen bij de K- en R-MATIC: daar haalt en brengt hij pallets. */
  werk?: Werkpunten
}

/**
 * Eén truck per hotspot, in het gangpad van die hotspot en van het juiste
 * model — zo staat de K-Matic er ook echt als de camera naar de K-Matic vliegt.
 *
 * De werkgang blijft bewust vlak bij de hotspot (zie WERKGANG). Reed de truck
 * de hele hal door, dan was hij op het scherm "De trucks komen tot leven" het
 * grootste deel van de tijd een stip in de verte of helemaal uit beeld.
 *
 * K-Matic en R-Matic werken in de stelling aan de +z-kant van hun gangpad:
 * werkpunt A zo dicht bij de hotspotcamera als `MIN_AFSTAND` toelaat, B een
 * paar vakken verder. Allebei recht voor het midden van een vak, want daar
 * staat de pallet, en niet in de dwarsgang: daar staat geen ligger.
 */
export function maakWerkgangen(ind: Indeling, aantal: number): Werkgang[] {
  return HOTSPOTS.slice(0, aantal).map((h, i) => {
    const gang: Werkgang = {
      model: h.truck,
      gangpad: ind.gangpadZ(h.gangpad),
      anker: h.x + WERKGANG.anker,
      bereik: WERKGANG.bereik,
      snelheid: 0.05 + (i % 3) * 0.008,
      fase: (i * 0.27) % 1,
      richting: i % 2 === 0 ? 1 : -1,
    }
    const werkpunt = WERKPUNT[h.truck]
    if (werkpunt === undefined) return gang

    const { bays } = ind.cfg
    const dg = ind.dwarsgang
    // Vak A: de camera staat aan de −x-kant van de hotspot, de truck komt
    // naar hem toe (vorken voorop) en staat dan met zijn oorsprong werkpunt
    // meter verder dan het vak.
    const camera = hotspotStand(ind, h.x, h.gangpad).positie[0]
    const x = gang.anker - WERKGANG.bereik - werkpunt
    let a = Math.min(Math.max(Math.round((x - ind.bayX(0)) / STELLING.bayBreedte), 0), bays - 1)
    while (ind.bayX(a) + werkpunt - camera < (MIN_AFSTAND[h.truck] ?? 0) && a < bays - 1) a++
    if (a === dg) a += a > 0 ? -1 : 1

    // Vak B verderop, of als dat niet past terug. De rit tussen A en B mag de
    // dwarsgang niet kruisen: daar rijdt de C-MATIC. Aan de +x-kant twee
    // vakken marge, want daar steekt de K-MATIC 4 m achter zijn vorken uit.
    const kruist = (van: number, tot: number) => dg !== null && dg >= Math.min(van, tot) - 1 && dg <= Math.max(van, tot) + 2
    const kandidaten = [...AFSTANDEN.map((d) => a + d), ...AFSTANDEN.map((d) => a - d)]
    const b = kandidaten.find((b) => b >= 0 && b < bays && !kruist(a, b))
    if (b === undefined) return gang

    const xa = ind.bayX(a)
    const xb = ind.bayX(b)
    return {
      ...gang,
      anker: (xa + xb) / 2 + werkpunt,
      bereik: Math.abs(xb - xa) / 2,
      richting: -1,
      werk: { a: xa, b: xb, vakken: [a, b], stelling: gang.gangpad + STELLING_OFFSET },
    }
  })
}

/** De vakken die `Lading` vrijhoudt: daar zetten de trucks hun eigen pallets. */
export function werkvakken(gangen: Werkgang[]) {
  return gangen.flatMap((g) =>
    g.werk
      ? g.werk.vakken.flatMap((bay) => [0, 1].map((niveau) => ({ z: g.werk!.stelling, bay, niveau })))
      : [],
  )
}
